import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { LogOut, Mail, Phone, RefreshCw, Trash2, Search, Inbox, CreditCard, Library as LibraryIcon, BookOpen, Layout, Users } from "lucide-react";

const STATUSES = ["new", "contacted", "converted", "archived"];

const statusStyles = {
    new: "bg-coral text-ink",
    contacted: "bg-sun text-ink",
    converted: "bg-sage text-ink",
    archived: "bg-white/15 text-ink/65",
};

const paymentStatusStyles = {
    paid: "bg-sage text-ink",
    pending: "bg-sun text-ink",
    initiated: "bg-white/15 text-ink/75",
    unpaid: "bg-coral text-ink",
    expired: "bg-white/15 text-ink/60",
    complete: "bg-sage text-ink",
};

const subStatusStyles = {
    active: "bg-sage text-ink",
    past_due: "bg-sun text-ink",
    canceled: "bg-coral text-white",
    cancelled: "bg-coral text-white",
    trialing: "bg-sun text-ink",
    unpaid: "bg-coral text-white",
    unknown: "bg-white/15 text-ink/75",
};

export default function AdminDashboard() {
    const { user, logout, loading } = useAuth();
    const nav = useNavigate();
    const [tab, setTab] = useState("leads");
    const [leads, setLeads] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [subMeta, setSubMeta] = useState({ active_count: 0, mrr: 0, live_status_available: false });
    const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0, archived: 0 });
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [active, setActive] = useState(null);

    useEffect(() => {
        if (!loading && (user === false || !user)) nav("/admin/login", { replace: true });
    }, [user, loading, nav]);

    async function refresh() {
        setBusy(true);
        try {
            const [r1, r2, r3, r4] = await Promise.all([
                api.get("/leads"),
                api.get("/leads/stats"),
                api.get("/purchases"),
                api.get("/admin/subscriptions"),
            ]);
            setLeads(r1.data);
            setStats(r2.data);
            setPurchases(r3.data);
            setSubscribers(r4.data.subscriptions || []);
            setSubMeta({
                active_count: r4.data.active_count ?? 0,
                mrr: r4.data.mrr ?? 0,
                live_status_available: !!r4.data.live_status_available,
            });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (user && user !== false) refresh();
        // eslint-disable-next-line
    }, [user]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        const list = tab === "leads" ? leads : tab === "subscribers" ? subscribers : purchases;
        return list
            .filter((l) => {
                if (tab === "leads") return filter === "all" ? true : l.status === filter;
                if (tab === "subscribers") return filter === "all" ? true : l.sub_status === filter;
                return filter === "all" ? true : (l.payment_status || l.status) === filter;
            })
            .filter((l) => {
                if (!term) return true;
                const fields =
                    tab === "leads"
                        ? [l.name, l.email, l.business_name, l.message, l.package]
                        : [l.email, l.package_label, l.package_id, l.session_id];
                return fields.filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
            });
    }, [leads, purchases, subscribers, filter, q, tab]);

    async function updateStatus(id, status) {
        try {
            await api.patch(`/leads/${id}`, { status });
            toast.success(`Marked as ${status}`);
            refresh();
            if (active && active.id === id) setActive({ ...active, status });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    }

    async function remove(id) {
        if (!window.confirm("Delete this lead permanently?")) return;
        try {
            await api.delete(`/leads/${id}`);
            toast.success("Lead deleted");
            setActive(null);
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    }

    const purchaseTotal = useMemo(() => {
        return purchases
            .filter((p) => (p.payment_status || p.status) === "paid")
            .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    }, [purchases]);

    if (loading || user === null) {
        return (
            <div className="grid min-h-screen place-items-center bg-page text-ink/60" data-testid="admin-loading">
                Loading control room…
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-page text-ink" data-testid="admin-dashboard">
            <header className="sticky top-0 z-30 border-b-2 border-ink/15 bg-page/95 backdrop-blur">
                <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-5 py-4 lg:px-8">
                    <Link to="/" className="flex items-center gap-2" data-testid="admin-brand">
                        <span className="grid h-7 w-7 place-items-center bg-coral text-ink font-display font-black">
                            s
                        </span>
                        <div>
                            <div className="font-display text-base font-black uppercase tracking-tight">
                                Control Room
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-ink/45">
                                Simple Digital
                            </div>
                        </div>
                    </Link>

                    <div className="flex items-center gap-3">
                        <span className="hidden text-xs uppercase tracking-widest text-ink/50 lg:inline">
                            {user && user.email}
                        </span>
                        <Link
                            to="/admin/library"
                            className="inline-flex items-center gap-2 border-2 border-ink/20 px-3 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                            data-testid="admin-library-link"
                        >
                            <LibraryIcon className="h-3.5 w-3.5" /> Library
                        </Link>
                        <Link
                            to="/admin/booklets"
                            className="inline-flex items-center gap-2 border-2 border-ink/20 px-3 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                            data-testid="admin-booklets-link"
                        >
                            <BookOpen className="h-3.5 w-3.5" /> Booklets
                        </Link>
                        <Link
                            to="/admin/site-content"
                            className="inline-flex items-center gap-2 border-2 border-ink/20 px-3 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                            data-testid="admin-site-content-link"
                        >
                            <Layout className="h-3.5 w-3.5" /> Site content
                        </Link>
                        <button
                            onClick={refresh}
                            disabled={busy}
                            className="grid h-9 w-9 place-items-center border-2 border-ink/20 text-ink/70 hover:border-coral hover:text-coral"
                            data-testid="admin-refresh"
                            aria-label="Refresh"
                        >
                            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
                        </button>
                        <button
                            onClick={async () => {
                                await logout();
                                nav("/admin/login", { replace: true });
                            }}
                            className="inline-flex items-center gap-2 border-2 border-ink/20 px-3 py-2 font-display text-xs font-bold uppercase tracking-wider text-ink hover:border-coral hover:text-coral"
                            data-testid="admin-logout"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[1400px] px-5 py-10 lg:px-8">
                <div className="mb-6 inline-flex border-2 border-ink/20 bg-white p-1" data-testid="admin-tabs">
                    <button
                        onClick={() => {
                            setTab("leads");
                            setFilter("all");
                        }}
                        className={`inline-flex items-center gap-2 px-4 py-2 font-display text-sm font-bold uppercase tracking-wider transition-all ${
                            tab === "leads" ? "bg-coral text-ink" : "text-ink/65 hover:text-ink"
                        }`}
                        data-testid="tab-leads"
                    >
                        <Inbox className="h-4 w-4" /> Leads ({leads.length})
                    </button>
                    <button
                        onClick={() => {
                            setTab("purchases");
                            setFilter("all");
                        }}
                        className={`inline-flex items-center gap-2 px-4 py-2 font-display text-sm font-bold uppercase tracking-wider transition-all ${
                            tab === "purchases" ? "bg-coral text-ink" : "text-ink/65 hover:text-ink"
                        }`}
                        data-testid="tab-purchases"
                    >
                        <CreditCard className="h-4 w-4" /> Purchases ({purchases.length})
                    </button>
                    <button
                        onClick={() => {
                            setTab("subscribers");
                            setFilter("all");
                        }}
                        className={`inline-flex items-center gap-2 px-4 py-2 font-display text-sm font-bold uppercase tracking-wider transition-all ${
                            tab === "subscribers" ? "bg-coral text-ink" : "text-ink/65 hover:text-ink"
                        }`}
                        data-testid="tab-subscribers"
                    >
                        <Users className="h-4 w-4" /> Subscribers ({subscribers.length})
                    </button>
                </div>

                {tab === "leads" ? (
                    <section className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="admin-stats">
                        {[
                            { k: "total", label: "Total" },
                            { k: "new", label: "New" },
                            { k: "contacted", label: "Contacted" },
                            { k: "converted", label: "Converted" },
                            { k: "archived", label: "Archived" },
                        ].map((s) => (
                            <div
                                key={s.k}
                                className={`border-2 border-ink/15 bg-white p-5 ${
                                    s.k === "new" ? "frame-coral" : ""
                                }`}
                                data-testid={`stat-${s.k}`}
                            >
                                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink/55">
                                    {s.label}
                                </div>
                                <div className="mt-2 font-display text-4xl font-black text-ink">
                                    {stats[s.k] ?? 0}
                                </div>
                            </div>
                        ))}
                    </section>
                ) : tab === "subscribers" ? (
                    <section className="mb-8 grid gap-4 sm:grid-cols-3" data-testid="admin-subscriber-stats">
                        <Stat label="Active subscribers" value={subMeta.active_count} highlight />
                        <Stat label="Monthly revenue (MRR)" value={`$${Number(subMeta.mrr).toFixed(0)}`} />
                        <Stat
                            label="Renewal status"
                            value={subMeta.live_status_available ? "Live" : "Estimated"}
                        />
                    </section>
                ) : (
                    <section className="mb-8 grid gap-4 sm:grid-cols-3" data-testid="admin-purchase-stats">
                        <Stat label="Total sessions" value={purchases.length} />
                        <Stat
                            label="Paid"
                            value={
                                purchases.filter((p) => (p.payment_status || p.status) === "paid")
                                    .length
                            }
                            highlight
                        />
                        <Stat label="Revenue (paid · all ccy)" value={`$${purchaseTotal.toFixed(2)}`} />
                    </section>
                )}

                <section className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap gap-2" data-testid="admin-filters">
                        <FilterBtn active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
                            All
                        </FilterBtn>
                        {(tab === "leads"
                            ? STATUSES
                            : tab === "subscribers"
                                ? ["active", "past_due", "canceled", "unknown"]
                                : ["paid", "pending", "initiated", "expired"]
                        ).map(
                            (s) => (
                                <FilterBtn
                                    key={s}
                                    active={filter === s}
                                    onClick={() => setFilter(s)}
                                    testid={`filter-${s}`}
                                >
                                    {s}
                                </FilterBtn>
                            ),
                        )}
                    </div>
                    <div className="relative w-full sm:w-80">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={tab === "leads" ? "Search name, email…" : "Search email, package…"}
                            className="w-full border-2 border-ink/20 bg-white py-2.5 pl-10 pr-3 text-sm text-ink placeholder:text-ink/35 focus:border-coral focus:outline-none"
                            data-testid="admin-search"
                        />
                    </div>
                </section>

                <section className="border-2 border-ink/15 bg-white" data-testid="admin-leads-table">
                    {tab === "subscribers" ? (
                        <>
                            <div className="grid grid-cols-12 gap-3 border-b-2 border-ink/15 bg-white px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">
                                <div className="col-span-3">Email</div>
                                <div className="col-span-3">Plan</div>
                                <div className="col-span-2">Per month</div>
                                <div className="col-span-2">Next renewal</div>
                                <div className="col-span-2 text-right">Status</div>
                            </div>

                            {filtered.length === 0 && (
                                <Empty>
                                    {subscribers.length === 0
                                        ? "No subscribers yet. Once someone starts a monthly plan, they'll show up here."
                                        : "No subscribers match your filter."}
                                </Empty>
                            )}

                            {filtered.map((s) => (
                                <button
                                    key={s.session_id}
                                    onClick={() => setActive({ ...s, _type: "subscriber" })}
                                    className="grid w-full grid-cols-12 gap-3 border-b border-ink/10 px-5 py-4 text-left transition-colors hover:bg-white"
                                    data-testid={`subscriber-row-${s.session_id}`}
                                >
                                    <div className="col-span-3 truncate text-sm text-ink">
                                        {s.email || <span className="text-ink/45">— (guest)</span>}
                                    </div>
                                    <div className="col-span-3 text-sm text-ink/80">{s.package_label}</div>
                                    <div className="col-span-2 font-display text-sm font-bold text-ink">
                                        {s.currency} ${Number(s.amount).toFixed(0)}
                                    </div>
                                    <div className="col-span-2 text-xs text-ink/55">
                                        {s.next_renewal
                                            ? new Date(s.next_renewal).toLocaleDateString("en-NZ", {
                                                  day: "2-digit",
                                                  month: "short",
                                                  year: "numeric",
                                              })
                                            : "—"}
                                        {s.status_source === "estimated" && (
                                            <span className="block text-[10px] text-ink/40">estimated</span>
                                        )}
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <span
                                            className={`inline-block border border-ink/20 px-2 py-1 font-display text-[10px] font-bold uppercase tracking-widest ${
                                                subStatusStyles[s.sub_status] || subStatusStyles.unknown
                                            }`}
                                            data-testid={`subscriber-status-${s.session_id}`}
                                        >
                                            {s.sub_status}
                                            {s.cancel_at_period_end ? " · ending" : ""}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </>
                    ) : tab === "leads" ? (
                        <>
                            <div className="grid grid-cols-12 gap-3 border-b-2 border-ink/15 bg-white px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">
                                <div className="col-span-3">Name / Business</div>
                                <div className="col-span-3">Email</div>
                                <div className="col-span-2">Source</div>
                                <div className="col-span-2">Date</div>
                                <div className="col-span-2 text-right">Status</div>
                            </div>

                            {filtered.length === 0 && (
                                <Empty>
                                    {leads.length === 0
                                        ? "No leads yet. Once someone fills the contact form or downloads the checklist, they'll show up here."
                                        : "No leads match your filter."}
                                </Empty>
                            )}

                            {filtered.map((l) => (
                                <button
                                    key={l.id}
                                    onClick={() => setActive({ ...l, _type: "lead" })}
                                    className="grid w-full grid-cols-12 gap-3 border-b border-ink/10 px-5 py-4 text-left transition-colors hover:bg-white"
                                    data-testid={`lead-row-${l.id}`}
                                >
                                    <div className="col-span-3">
                                        <div className="font-display text-sm font-bold text-ink">{l.name}</div>
                                        <div className="text-xs text-ink/55">{l.business_name || "—"}</div>
                                    </div>
                                    <div className="col-span-3 truncate text-sm text-ink/75">{l.email}</div>
                                    <div className="col-span-2 text-sm text-ink/70">
                                        {l.source === "lead_magnet" ? "Checklist" : l.package || "Contact"}
                                    </div>
                                    <div className="col-span-2 text-xs text-ink/55">
                                        {new Date(l.created_at).toLocaleDateString("en-NZ", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                        })}
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <span
                                            className={`inline-block border border-ink/20 px-2 py-1 font-display text-[10px] font-bold uppercase tracking-widest ${
                                                statusStyles[l.status] || statusStyles.new
                                            }`}
                                        >
                                            {l.status}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-3 border-b-2 border-ink/15 bg-white px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">
                                <div className="col-span-3">Email</div>
                                <div className="col-span-3">Package</div>
                                <div className="col-span-2">Amount</div>
                                <div className="col-span-2">Date</div>
                                <div className="col-span-2 text-right">Payment</div>
                            </div>

                            {filtered.length === 0 && (
                                <Empty>
                                    {purchases.length === 0
                                        ? "No purchases yet. Once someone completes Stripe checkout, it'll appear here."
                                        : "No purchases match your filter."}
                                </Empty>
                            )}

                            {filtered.map((p) => (
                                <button
                                    key={p.session_id}
                                    onClick={() => setActive({ ...p, _type: "purchase" })}
                                    className="grid w-full grid-cols-12 gap-3 border-b border-ink/10 px-5 py-4 text-left transition-colors hover:bg-white"
                                    data-testid={`purchase-row-${p.session_id}`}
                                >
                                    <div className="col-span-3 truncate text-sm text-ink">
                                        {p.email || <span className="text-ink/45">— (guest)</span>}
                                    </div>
                                    <div className="col-span-3 text-sm text-ink/80">{p.package_label}</div>
                                    <div className="col-span-2 font-display text-sm font-bold text-ink">
                                        {p.currency} ${Number(p.amount).toFixed(2)}
                                    </div>
                                    <div className="col-span-2 text-xs text-ink/55">
                                        {new Date(p.created_at).toLocaleDateString("en-NZ", {
                                            day: "2-digit",
                                            month: "short",
                                        })}
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <span
                                            className={`inline-block border border-ink/20 px-2 py-1 font-display text-[10px] font-bold uppercase tracking-widest ${
                                                paymentStatusStyles[p.payment_status] ||
                                                paymentStatusStyles[p.status] ||
                                                paymentStatusStyles.pending
                                            }`}
                                        >
                                            {p.payment_status || p.status}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </>
                    )}
                </section>
            </main>

            {active && active._type === "lead" && (
                <Drawer onClose={() => setActive(null)} testid="lead-drawer">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-coral">Lead</div>
                    <h3 className="mt-1 font-display text-3xl font-black uppercase tracking-tight text-ink">
                        {active.name}
                    </h3>
                    {active.business_name && (
                        <div className="mt-1 text-sm text-ink/60">{active.business_name}</div>
                    )}

                    <div className="mt-6 grid gap-3 border-y-2 border-ink/15 py-5 text-sm">
                        <a
                            href={`mailto:${active.email}`}
                            className="flex items-center gap-3 text-ink/85 hover:text-coral"
                        >
                            <Mail className="h-4 w-4" /> {active.email}
                        </a>
                        {active.phone && (
                            <a
                                href={`tel:${active.phone}`}
                                className="flex items-center gap-3 text-ink/85 hover:text-coral"
                            >
                                <Phone className="h-4 w-4" /> {active.phone}
                            </a>
                        )}
                        <KV k="Source" v={active.source === "lead_magnet" ? "7-Day Checklist" : "Contact form"} />
                        <KV k="Package" v={active.package || "—"} />
                        <KV k="Submitted" v={new Date(active.created_at).toLocaleString("en-NZ")} />
                    </div>

                    <div className="mt-6">
                        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-ink/55">
                            Message
                        </div>
                        <p className="whitespace-pre-wrap text-base leading-relaxed text-ink">
                            {active.message}
                        </p>
                    </div>

                    <div className="mt-auto pt-8">
                        <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.22em] text-ink/55">
                            Set status
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {STATUSES.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => updateStatus(active.id, s)}
                                    className={`border-2 border-ink/20 px-3 py-2 font-display text-[11px] font-bold uppercase tracking-widest transition-all ${
                                        active.status === s
                                            ? "bg-coral border-coral text-ink"
                                            : "bg-white text-ink hover:bg-white"
                                    }`}
                                    data-testid={`lead-status-${s}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => remove(active.id)}
                            className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-coral hover:text-coral-hover"
                            data-testid="lead-delete"
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Delete lead
                        </button>
                    </div>
                </Drawer>
            )}

            {active && active._type === "purchase" && (
                <Drawer onClose={() => setActive(null)} testid="purchase-drawer">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-coral">Purchase</div>
                    <h3 className="mt-1 font-display text-3xl font-black uppercase tracking-tight text-ink">
                        {active.package_label}
                    </h3>
                    <div className="mt-1 font-display text-2xl font-bold text-ink">
                        {active.currency} ${Number(active.amount).toFixed(2)}
                    </div>

                    <div className="mt-6 grid gap-3 border-y-2 border-ink/15 py-5 text-sm">
                        <KV k="Email" v={active.email || "— (guest)"} />
                        <KV k="Session" v={active.session_id} mono />
                        <KV
                            k="Payment status"
                            v={active.payment_status || active.status}
                            highlight={(active.payment_status || active.status) === "paid"}
                        />
                        <KV k="Created" v={new Date(active.created_at).toLocaleString("en-NZ")} />
                        {active.updated_at && (
                            <KV k="Updated" v={new Date(active.updated_at).toLocaleString("en-NZ")} />
                        )}
                    </div>
                </Drawer>
            )}

            {active && active._type === "subscriber" && (
                <Drawer onClose={() => setActive(null)} testid="subscriber-drawer">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-coral">
                        Subscriber
                    </div>
                    <h3 className="mt-1 font-display text-3xl font-black uppercase tracking-tight text-ink">
                        {active.package_label}
                    </h3>
                    <div className="mt-1 font-display text-2xl font-bold text-ink">
                        {active.currency} ${Number(active.amount).toFixed(0)}/mo
                    </div>

                    <div className="mt-6 grid gap-3 border-y-2 border-ink/15 py-5 text-sm">
                        <KV k="Email" v={active.email || "— (guest)"} />
                        <KV
                            k="Status"
                            v={`${active.sub_status}${active.cancel_at_period_end ? " · cancels at period end" : ""}`}
                            highlight={active.sub_status === "active" && !active.cancel_at_period_end}
                        />
                        <KV
                            k="Next renewal"
                            v={
                                active.next_renewal
                                    ? new Date(active.next_renewal).toLocaleString("en-NZ")
                                    : "—"
                            }
                        />
                        <KV
                            k="Renewal info"
                            v={active.status_source === "stripe" ? "Live from Stripe" : "Estimated (monthly from signup)"}
                        />
                        <KV k="Started" v={new Date(active.started_at).toLocaleString("en-NZ")} />
                        <KV k="Session" v={active.session_id} mono />
                        {active.stripe_subscription_id && (
                            <KV k="Stripe sub" v={active.stripe_subscription_id} mono />
                        )}
                    </div>

                    <p className="mt-5 text-xs leading-relaxed text-ink/55">
                        Renewals, retries and card updates are handled by Stripe automatically. To cancel or
                        refund a subscriber, find them in your Stripe dashboard.
                    </p>
                </Drawer>
            )}
        </div>
    );
}

function FilterBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            className={`border-2 border-ink/20 px-3 py-1.5 font-display text-[11px] font-bold uppercase tracking-widest transition-all ${
                active ? "bg-coral border-coral text-ink" : "bg-white text-ink hover:bg-white"
            }`}
            data-testid={testid}
        >
            {children}
        </button>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div className={`border-2 border-ink/15 bg-white p-5 ${highlight ? "frame-coral" : ""}`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink/55">{label}</div>
            <div className="mt-2 font-display text-4xl font-black text-ink">{value}</div>
        </div>
    );
}

function Empty({ children }) {
    return (
        <div className="px-5 py-16 text-center text-sm text-ink/55" data-testid="admin-empty">
            {children}
        </div>
    );
}

function KV({ k, v, mono, highlight }) {
    return (
        <div className="flex flex-wrap items-baseline gap-2 text-ink/80">
            <span className="text-[10px] uppercase tracking-widest text-ink/45">{k}</span>
            <span
                className={`${mono ? "font-mono text-xs" : ""} ${
                    highlight ? "bg-coral px-2 py-0.5 font-bold text-ink" : ""
                }`}
            >
                {v}
            </span>
        </div>
    );
}

function Drawer({ children, onClose, testid }) {
    return (
        <div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid={`${testid}-backdrop`}
        >
            <aside
                className="fixed right-0 top-0 flex h-full w-full max-w-xl flex-col overflow-y-auto border-l-2 border-ink/20 bg-white p-8"
                onClick={(e) => e.stopPropagation()}
                data-testid={testid}
            >
                <button
                    onClick={onClose}
                    className="absolute right-6 top-6 grid h-9 w-9 place-items-center border-2 border-ink/20 text-ink/65 hover:border-coral hover:text-coral"
                    aria-label="Close"
                    data-testid={`${testid}-close`}
                >
                    ✕
                </button>
                {children}
            </aside>
        </div>
    );
}
